import { useState, useEffect } from 'react'
import axios from 'axios'

const UserManagement = () => {
  const [users, setUsers] = useState([])
  const [permissions, setPermissions] = useState({})
  const [predefinedRoles, setPredefinedRoles] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [showUserModal, setShowUserModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [sortField, setSortField] = useState('name')
  const [sortDirection, setSortDirection] = useState('asc')

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    permissions: [],
    customRoleName: '',
    isActive: true,
  })

  // Fetch users and system permissions
  const fetchUsers = async () => {
    try {
      setLoading(true)
      setError('')
      const [usersResponse, permissionsResponse] = await Promise.all([
        axios.get('/api/users'),
        axios.get('/api/users/system/permissions'),
      ])
      setUsers(usersResponse.data.users || [])
      setPermissions(permissionsResponse.data.permissions || {})
      setPredefinedRoles(permissionsResponse.data.predefinedRoles || {})
    } catch (err) {
      setError('Failed to load users. Please try again.')
      console.error('Error fetching users:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  // Auto-clear success/error messages
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('')
        setError('')
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [success, error])

  // Filter and search users
  const filteredUsers = users
    .filter((user) => {
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase()
        return (
          user.name?.toLowerCase().includes(term) ||
          user.email?.toLowerCase().includes(term) ||
          user.customRoleName?.toLowerCase().includes(term) ||
          user.roleDisplay?.toLowerCase().includes(term)
        )
      }
      return true
    })
    .filter((user) => {
      // Role filter
      if (roleFilter !== 'all') {
        return user.role === roleFilter
      }
      return true
    })
    .filter((user) => {
      // Status filter
      if (statusFilter !== 'all') {
        return statusFilter === 'active' ? user.isActive : !user.isActive
      }
      return true
    })
    .sort((a, b) => {
      let aVal, bVal

      if (sortField === 'name') {
        aVal = a.name?.toLowerCase() || ''
        bVal = b.name?.toLowerCase() || ''
      } else if (sortField === 'email') {
        aVal = a.email?.toLowerCase() || ''
        bVal = b.email?.toLowerCase() || ''
      } else if (sortField === 'role') {
        aVal = a.roleDisplay?.toLowerCase() || ''
        bVal = b.roleDisplay?.toLowerCase() || ''
      } else {
        aVal = a[sortField] || ''
        bVal = b[sortField] || ''
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0
      }
    })

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Open create user modal
  const handleCreateUser = () => {
    setSelectedUser(null)
    setFormData({
      name: '',
      email: '',
      password: '',
      permissions: [],
      customRoleName: '',
      isActive: true,
    })
    setShowUserModal(true)
  }

  // Open edit user modal
  const handleEditUser = (user) => {
    setSelectedUser(user)
    setFormData({
      name: user.name,
      email: user.email,
      password: '', // Don't pre-fill password
      permissions: user.permissions || [],
      customRoleName: user.customRoleName || '',
      isActive: user.isActive !== false,
    })
    setShowUserModal(true)
  }

  // Handle permission toggle
  const togglePermission = (permission) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission],
    }))
  }

  // Apply predefined role
  const applyPredefinedRole = (roleKey) => {
    const role = predefinedRoles[roleKey]
    if (role) {
      setFormData((prev) => ({
        ...prev,
        permissions: [...role.permissions],
        customRoleName: role.name,
      }))
    }
  }

  // Submit user form
  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    try {
      if (selectedUser) {
        // Update user
        const updateData = {
          name: formData.name,
          email: formData.email,
          permissions: formData.permissions,
          customRoleName: formData.customRoleName,
          isActive: formData.isActive,
        }
        // Only include password if it's being changed
        if (formData.password) {
          updateData.password = formData.password
        }
        await axios.put(`/api/users/${selectedUser._id}`, updateData)
        setSuccess('User updated successfully')
      } else {
        // Create user
        if (!formData.password) {
          setError('Password is required for new users')
          return
        }
        await axios.post('/api/users', formData)
        setSuccess('User created successfully')
      }
      setShowUserModal(false)
      fetchUsers()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save user. Please try again.')
      console.error('Error saving user:', err)
    }
  }

  // Handle delete user
  const handleDeleteUser = async () => {
    try {
      setError('')
      await axios.delete(`/api/users/${selectedUser._id}`)
      setSuccess('User deleted successfully')
      setShowDeleteConfirm(false)
      setSelectedUser(null)
      fetchUsers()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete user. Please try again.')
      console.error('Error deleting user:', err)
    }
  }

  // Get status badge
  const getStatusBadge = (isActive) => {
    return isActive
      ? 'bg-green-100 text-green-800'
      : 'bg-gray-100 text-gray-800'
  }

  // Get role badge color
  const getRoleBadge = (user) => {
    if (user.permissions?.includes(permissions.ADMIN)) {
      return 'bg-red-100 text-red-800'
    }
    return 'bg-primary-100 text-primary-800'
  }

  // Sort icon
  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      )
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    )
  }

  // Group permissions by category
  const groupedPermissions = () => {
    const groups = {
      'System Administration': [permissions.ADMIN, permissions.MANAGE_USERS, permissions.MANAGE_ROLES],
      'Member Management': [permissions.MANAGE_MEMBERS, permissions.VIEW_MEMBERS],
      'Facility Reservations': [
        permissions.MANAGE_FACILITY_RESERVATIONS,
        permissions.APPROVE_FACILITY_RESERVATIONS,
        permissions.VIEW_FACILITY_RESERVATIONS,
      ],
      'Tennis Reservations': [
        permissions.MANAGE_TENNIS_RESERVATIONS,
        permissions.APPROVE_TENNIS_RESERVATIONS,
        permissions.VIEW_TENNIS_RESERVATIONS,
      ],
      'Events': [permissions.MANAGE_EVENTS, permissions.VIEW_EVENTS],
      'Board Minutes': [permissions.MANAGE_MINUTES, permissions.VIEW_MINUTES],
      'Pool Settings': [permissions.MANAGE_POOL_SETTINGS],
      'Lifeguard Coverage': [
        permissions.MANAGE_LIFEGUARD_GAPS,
        permissions.VIEW_LIFEGUARD_GAPS,
      ],
    }

    return groups
  }

  const permissionLabels = {
    [permissions.ADMIN]: 'Full Admin Access',
    [permissions.MANAGE_USERS]: 'Manage Users',
    [permissions.MANAGE_ROLES]: 'Manage Roles',
    [permissions.MANAGE_MEMBERS]: 'Manage Members',
    [permissions.VIEW_MEMBERS]: 'View Members',
    [permissions.MANAGE_FACILITY_RESERVATIONS]: 'Manage Facility Reservations',
    [permissions.APPROVE_FACILITY_RESERVATIONS]: 'Approve Facility Reservations',
    [permissions.VIEW_FACILITY_RESERVATIONS]: 'View Facility Reservations',
    [permissions.MANAGE_TENNIS_RESERVATIONS]: 'Manage Tennis Reservations',
    [permissions.APPROVE_TENNIS_RESERVATIONS]: 'Approve Tennis Reservations',
    [permissions.VIEW_TENNIS_RESERVATIONS]: 'View Tennis Reservations',
    [permissions.MANAGE_EVENTS]: 'Manage Events',
    [permissions.VIEW_EVENTS]: 'View Events',
    [permissions.MANAGE_MINUTES]: 'Manage Board Minutes',
    [permissions.VIEW_MINUTES]: 'View Board Minutes',
    [permissions.MANAGE_POOL_SETTINGS]: 'Manage Pool Settings',
    [permissions.MANAGE_LIFEGUARD_GAPS]: 'Manage Lifeguard Coverage Gaps',
    [permissions.VIEW_LIFEGUARD_GAPS]: 'View Lifeguard Coverage Gaps',
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-1">Manage users, roles, and permissions</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchUsers}
            className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium flex items-center shadow-sm"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh
          </button>
          <button
            onClick={handleCreateUser}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium flex items-center shadow-lg"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add User
          </button>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">{success}</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search by name, email, role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>
          </div>

          {/* Role Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="member">Member</option>
              <option value="board">Board</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Results Count */}
        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredUsers.length} of {users.length} users
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    onClick={() => handleSort('name')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-1">
                      <span>Name</span>
                      <SortIcon field="name" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('email')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-1">
                      <span>Email</span>
                      <SortIcon field="email" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('role')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-1">
                      <span>Role</span>
                      <SortIcon field="role" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Permissions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadge(
                          user
                        )}`}
                      >
                        {user.roleDisplay || user.customRoleName || 'Custom Role'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600">
                        {user.permissions?.length || 0} permission{user.permissions?.length !== 1 ? 's' : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(
                          user.isActive
                        )}`}
                      >
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="text-primary-600 hover:text-primary-900 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          setSelectedUser(user)
                          setShowDeleteConfirm(true)
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              <p className="mt-2 text-sm text-gray-600">No users found</p>
            </div>
          )}
        </div>
      )}

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => setShowUserModal(false)}>
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />

            <div
              className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-6 pt-5 pb-4">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">
                      {selectedUser ? 'Edit User' : 'Create User'}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowUserModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="space-y-6">
                    {/* Basic Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Password {!selectedUser && '*'}
                        </label>
                        <input
                          type="password"
                          required={!selectedUser}
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          placeholder={selectedUser ? 'Leave blank to keep current password' : ''}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Custom Role Name</label>
                        <input
                          type="text"
                          value={formData.customRoleName}
                          onChange={(e) => setFormData({ ...formData, customRoleName: e.target.value })}
                          placeholder="e.g., Pool Manager"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                        />
                      </div>
                    </div>

                    {/* Status */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isActive"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                        Active User
                      </label>
                    </div>

                    {/* Predefined Roles */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Quick Apply Role</label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {Object.entries(predefinedRoles).map(([key, role]) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => applyPredefinedRole(key)}
                            className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-left"
                          >
                            <div className="font-medium">{role.name}</div>
                            <div className="text-xs text-gray-500">{role.permissions.length} permissions</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Permissions */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">Permissions</label>
                      <div className="border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                        {Object.entries(groupedPermissions()).map(([category, perms]) => (
                          <div key={category} className="mb-4 last:mb-0">
                            <div className="font-medium text-sm text-gray-700 mb-2">{category}</div>
                            <div className="space-y-2 ml-2">
                              {perms.filter(Boolean).map((permission) => (
                                <div key={permission} className="flex items-center">
                                  <input
                                    type="checkbox"
                                    id={`perm-${permission}`}
                                    checked={formData.permissions.includes(permission)}
                                    onChange={() => togglePermission(permission)}
                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                  />
                                  <label
                                    htmlFor={`perm-${permission}`}
                                    className="ml-2 block text-sm text-gray-700"
                                  >
                                    {permissionLabels[permission] || permission}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 text-sm text-gray-500">
                        Selected: {formData.permissions.length} permission{formData.permissions.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowUserModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    {selectedUser ? 'Update User' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => setShowDeleteConfirm(false)}>
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />

            <div
              className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white px-6 pt-5 pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Delete User</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete <strong>{selectedUser.name}</strong>? This action cannot be
                        undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserManagement
